<script setup lang="ts">
import BaseButton from '@components/quiz_v2/BaseButton/BaseButton.vue';
import BaseCheckbox from '@components/quiz_v2/BaseCheckbox/BaseCheckbox.vue';
import BaseInput from '@components/quiz_v2/BaseInput/BaseInput.vue';
import BaseSelect from '@components/quiz_v2/BaseSelect/BaseSelect.vue';
import BaseSwitch from '@components/quiz_v2/BaseSwitch/BaseSwitch.vue';
import { optionsPhone, optionsPostcode } from '@composables/useMaskOptions';
import { StepCheckout } from '@models/quiz';
import { useCheckoutStore } from '@stores/checkout/checkoutStore';
import { vMaska } from 'maska';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import IconArrowLeft from '../../assets/icons/arrow-left.svg';
import IconArrowRight from '../../assets/icons/arrow-right.svg';
import { useAutocomplete } from '../../composables/useAutocomplete';

defineProps<{
  content: StepCheckout;
}>();

const emit = defineEmits<{
  'next': [];
  'back': [];
}>();

const checkoutStore = useCheckoutStore();
const { autocomplete } = useAutocomplete();

const {
  mainProduct,
  checkoutForm,
  checkout$,
  isBillingAddressSame,
  consentForm,
} = storeToRefs(checkoutStore);

const { scrollToError } = checkoutStore;

const shippingAddressInput = ref<InstanceType<typeof BaseInput> | null>(null);
const billingAddressInput = ref<InstanceType<typeof BaseInput> | null>(null);

onMounted(() => {
  autocomplete(shippingAddressInput.value?.input!, (places) => {
    checkoutForm.value.shippingAddress.line1 = [
      places.streetNumber?.longName,
      places.route?.longName,
    ].filter(Boolean).join(' ');

    checkoutForm.value.shippingAddress.city = places.locality?.longName ?? '';
    checkoutForm.value.shippingAddress.state_code = places.administrativeAreaLevel1?.shortName ?? '';

    checkoutForm.value.shippingAddress.zip = [
      places.postalCode?.longName,
      places.postalCodeSuffix?.longName,
    ].filter(Boolean).join('-');
  });
});

watch(isBillingAddressSame, async (isBillingAddressSame) => {
  if (isBillingAddressSame) {
    return;
  }

  await nextTick();

  autocomplete(billingAddressInput.value?.input!, (places) => {
    checkoutForm.value.billingAddress!.line1 = [
      places.streetNumber?.longName,
      places.route?.longName,
    ].filter(Boolean).join(' ');

    checkoutForm.value.billingAddress!.city = places.locality?.longName ?? '';
    checkoutForm.value.billingAddress!.state_code = places.administrativeAreaLevel1?.shortName ?? '';

    checkoutForm.value.billingAddress!.zip = [
      places.postalCode?.longName,
      places.postalCodeSuffix?.longName,
    ].filter(Boolean).join('-');
  });
});

const isInvalid = computed(() => checkout$.value.shippingAddress.$invalid || checkout$.value.billingAddress.$invalid);

const next = async () => {
  const validation = await Promise.all([
    checkout$.value.shippingAddress.$validate(),
    checkout$.value.billingAddress.$validate(),
  ]);

  if (validation.some((v) => !v)) {
    scrollToError();
    return;
  }

  emit('next');
};
</script>

<template>
  <div class="flex flex-col items-center">
    <div class="checkout-slide-left w-full max-w-[530px]">
      <div :class="{ 'validation-error': checkout$.shippingAddress.$error }">
        <h1 class="text-24 font-medium">
          If prescribed, where should we send your treatment?
        </h1>

        <form class="mt-24">
          <div class="flex flex-col gap-20">
            <BaseInput
              v-model="checkoutForm.shippingAddress.firstName"
              label="First Name"
              data-testpl="checkout-address-first-name-input"
              :error="checkout$.shippingAddress.firstName.$errors[0]?.$message"
            />

            <BaseInput
              v-model="checkoutForm.shippingAddress.lastName"
              label="Last Name"
              data-testpl="checkout-address-last-name-input"
              :error="checkout$.shippingAddress.lastName.$errors[0]?.$message"
            />

            <BaseInput
              ref="shippingAddressInput"
              v-model="checkoutForm.shippingAddress.line1"
              label="Address"
              data-testpl="checkout-address-line1-input"
              :error="checkout$.shippingAddress.line1.$errors[0]?.$message"
            />

            <BaseInput
              v-model="checkoutForm.shippingAddress.line2"
              label="Apartment, unit, suite, or floor #"
              data-testpl="checkout-address-line2-input"
            />

            <BaseInput
              v-model="checkoutForm.shippingAddress.city"
              label="City"
              data-testpl="checkout-address-city-input"
              :error="checkout$.shippingAddress.city.$errors[0]?.$message"
            />

            <BaseSelect
              v-model="checkoutForm.shippingAddress.state_code"
              :options="content.states"
              label="State"
              data-testpl="checkout-address-state-input"
              :error="checkout$.shippingAddress.state_code.$errors[0]?.$message"
            />

            <!-- TODO: Fix type -->
            <BaseInput
              v-model="checkoutForm.shippingAddress.zip"
              v-maska:[optionsPostcode]
              label="Zip Code"
              data-testpl="checkout-address-zip-code-input"
              :error="checkout$.shippingAddress.zip.$errors[0]?.$message"
            />

            <!-- TODO: Fix type -->
            <BaseInput
              v-model="checkoutForm.shippingAddress.phone"
              v-maska:[optionsPhone]
              label="Phone"
              data-testpl="checkout-address-phone-input"
              :error="checkout$.shippingAddress.phone.$errors[0]?.$message"
            />
          </div>

          <div
            class="mt-32 md:mt-36"
            :class="{ 'validation-error': checkout$.billingAddress.$error }"
          >
            <BaseSwitch
              v-model="isBillingAddressSame"
              class="mt-24"
              data-testpl="checkout-address-billing-switch"
            >
              Use this as my billing address
            </BaseSwitch>
          </div>

          <template v-if="checkoutForm.billingAddress">
            <h2 class="mt-40 text-24 font-medium">
              Add your billing address:
            </h2>

            <div class="mt-24 flex flex-col gap-20">
              <BaseInput
                v-model.trim="checkoutForm.billingAddress.firstName"
                label="First Name"
                data-testpl="checkout-address-first-name-input"
                :error="checkout$.billingAddress.firstName.$errors[0]?.$message"
              />

              <BaseInput
                v-model.trim="checkoutForm.billingAddress.lastName"
                label="Last Name"
                data-testpl="checkout-address-last-name-input"
                :error="checkout$.billingAddress.lastName.$errors[0]?.$message"
              />

              <BaseInput
                ref="billingAddressInput"
                v-model.trim="checkoutForm.billingAddress.line1"
                label="Address"
                data-testpl="checkout-address-line1-input"
                :error="checkout$.billingAddress.line1.$errors[0]?.$message"
              />

              <BaseInput
                v-model.trim="checkoutForm.billingAddress.line2"
                label="Apartment, unit, suite, or floor #"
                data-testpl="checkout-address-line2-input"
              />

              <BaseInput
                v-model.trim="checkoutForm.billingAddress.city"
                label="City"
                data-testpl="checkout-address-city-input"
                :error="checkout$.billingAddress.city.$errors[0]?.$message"
              />

              <BaseSelect
                v-model="checkoutForm.billingAddress.state_code"
                :options="content.states"
                label="State"
                data-testpl="checkout-address-state-input"
                :error="checkout$.billingAddress.state_code.$errors[0]?.$message"
              />

              <!-- TODO: Fix type -->
              <BaseInput
                v-model.trim="checkoutForm.billingAddress.zip"
                v-maska:[optionsPostcode]
                label="Zip Code"
                data-testpl="checkout-address-zip-code-input"
                :error="checkout$.billingAddress.zip.$errors[0]?.$message"
              />

              <!-- TODO: Fix type -->
              <BaseInput
                v-model.trim="checkoutForm.billingAddress.phone"
                v-maska:[optionsPhone]
                label="Phone"
                data-testpl="checkout-address-phone-input"
                :error="checkout$.billingAddress.phone.$errors[0]?.$message"
              />
            </div>
          </template>

          <div class="mt-20 flex flex-col gap-16">
            <BaseCheckbox v-model="consentForm.consentEmailGeneralMarketing">
              <span class="text-12 text-secondary-text">
                I'd like to receive tips, offers, and resources by email.
              </span>
            </BaseCheckbox>

            <BaseCheckbox v-model="consentForm.consentSmsMarketing">
              <span class="text-12 text-secondary-text">
                By checking this box, you agree to receive recurring marketing SMS from JRNYS.
                Msg & data rates may apply. Message frequency varies. Reply STOP to opt-out.
                Consent isn't required to purchase.
                <a class="underline" href="/privacy-policy" target="_blank">Privacy policy</a>
              </span>
            </BaseCheckbox>

            <BaseCheckbox v-model="consentForm.consentEmailPersonalizedHealth">
              <span class="text-12 text-secondary-text">
                I authorize JRNYS to use my personal health information (e.g., prescription,
                treatment status, or lab results) to personalize these communications. I understand
                this is optional and I can revoke this authorization at any time from my account.
              </span>
            </BaseCheckbox>

            <BaseCheckbox v-model="consentForm.consentSmsTransactional">
              <span class="text-12 text-secondary-text">
                By providing your number, you agree to receive SMS updates about your order,
                delivery, and account activity. Msg & data rates may apply. Reply STOP to opt out.
              </span>
            </BaseCheckbox>
          </div>
        </form>
      </div>

      <div class="mt-40 grid items-center gap-x-20 gap-y-16 md:mt-60 md:grid-cols-[185px,1fr]">
        <BaseButton
          :color="isInvalid ? 'primary' : 'black'"
          data-testpl="checkout-address-continue-btn"
          animated
          @click="next"
        >
          Continue with {{ mainProduct?.product.title }}

          <template #icon-right>
            <IconArrowRight />
          </template>
        </BaseButton>

        <BaseButton
          class="md:order-first"
          theme="outlined"
          data-testpl="checkout-address-back-btn"
          animated
          @click="emit('back')"
        >
          Back

          <template #icon-left>
            <IconArrowLeft />
          </template>
        </BaseButton>
      </div>
    </div>
  </div>
</template>
